import { AlertTriangle, HelpCircle, ListChecks } from 'lucide-react';

import type { ActivityDomain, ActivityStatus, TaskActivityItem } from '../model/activity';
import type { ActivityBadgeMeta, DomainKey, DomainStyles, StatusFilterOption } from '../model/activity-display';

export const PAGE_SIZE = 20;

export const ACTIVITY_DOMAINS: DomainKey[] = ['alert', 'task', 'question'];

export const DOMAIN_META: Record<DomainKey, DomainStyles> = {
  alert: {
    label: 'Alertas',
    shortLabel: 'Alerta',
    icon: AlertTriangle,
    chipBg: 'bg-red-50 text-red-800 hover:bg-red-100',
    chipActiveBg: 'bg-red-600 text-white',
    chipBadgeBg: 'bg-red-200/70 text-red-900',
    rowBorderColor: 'border-l-red-500',
    rowIconColor: 'text-red-600',
  },
  task: {
    label: 'Tareas',
    shortLabel: 'Tarea',
    icon: ListChecks,
    chipBg: 'bg-blue-50 text-blue-800 hover:bg-blue-100',
    chipActiveBg: 'bg-blue-600 text-white',
    chipBadgeBg: 'bg-blue-200/70 text-blue-900',
    rowBorderColor: 'border-l-blue-500',
    rowIconColor: 'text-blue-600',
  },
  question: {
    label: 'Preguntas',
    shortLabel: 'Pregunta',
    icon: HelpCircle,
    chipBg: 'bg-amber-50 text-amber-900 hover:bg-amber-100',
    chipActiveBg: 'bg-amber-600 text-white',
    chipBadgeBg: 'bg-amber-200/70 text-amber-900',
    rowBorderColor: 'border-l-amber-500',
    rowIconColor: 'text-amber-600',
  },
};

export const STATUS_FILTERS: StatusFilterOption[] = [
  { value: 'all', label: 'Todos los estados', domains: ['all', 'alert', 'task', 'question'] },
  { value: 'active', label: 'Activo / Pendiente', domains: ['all', 'alert', 'task', 'question'] },
  { value: 'resolved', label: 'Resuelto / Archivado', domains: ['all', 'alert', 'task', 'question'] },
  { value: 'archived', label: 'Archivado', domains: ['alert', 'task'] },
  { value: 'done', label: 'Completado', domains: ['task'] },
  { value: 'answered', label: 'Respondida', domains: ['question'] },
  { value: 'unanswered', label: 'Sin respuesta', domains: ['question'] },
];

export const STATUS_BADGE: Record<string, ActivityBadgeMeta> = {
  active: { bg: 'bg-green-100 text-green-800', label: 'Activa' },
  archived: { bg: 'bg-neutral-100 text-neutral-600', label: 'Archivada' },
  pending: { bg: 'bg-yellow-100 text-yellow-800', label: 'Pendiente' },
  done: { bg: 'bg-green-100 text-green-800', label: 'Completada' },
  unanswered: { bg: 'bg-orange-100 text-orange-800', label: 'Sin respuesta' },
  answered: { bg: 'bg-green-100 text-green-800', label: 'Respondida' },
};

export const PRIORITY_STYLES: Record<TaskActivityItem['priority'], ActivityBadgeMeta> = {
  low: { bg: 'bg-neutral-100 text-neutral-700', label: 'Baja' },
  medium: { bg: 'bg-blue-100 text-blue-800', label: 'Media' },
  high: { bg: 'bg-amber-100 text-amber-900', label: 'Alta' },
  urgent: { bg: 'bg-red-100 text-red-800', label: 'Urgente' },
};

export const isStatusValidForDomain = (status: ActivityStatus, domain: ActivityDomain): boolean =>
  STATUS_FILTERS.find((filter) => filter.value === status)?.domains.includes(domain) ?? false;
